import { createFileRoute, Link } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import {
  AlertTriangle,
  CalendarClock,
  Loader2,
  PauseCircle,
  ShieldQuestion,
  Trash2,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { getAdminOverview, PlatformApiError, type PlatformOverview } from '@/lib/api'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/platform/')({
  component: PlatformDashboard,
})

function PlatformDashboard() {
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'loaded'; overview: PlatformOverview }
    | { status: 'error'; message: string }
  >({ status: 'loading' })

  useEffect(() => {
    const ctrl = new AbortController()
    getAdminOverview(ctrl.signal)
      .then((overview) => setState({ status: 'loaded', overview }))
      .catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') return
        const message =
          err instanceof PlatformApiError && err.message ? err.message : 'Failed to load overview'
        setState({ status: 'error', message })
      })
    return () => ctrl.abort()
  }, [])

  if (state.status === 'loading') {
    return (
      <div className="flex items-center justify-center gap-2 rounded-2xl border border-border bg-card/60 py-12 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading overview…
      </div>
    )
  }
  if (state.status === 'error') {
    return (
      <div className="rounded-2xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
        {state.message}
      </div>
    )
  }

  const { overview } = state
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="space-y-6"
    >
      <KpiTiles overview={overview} />
      <div className="grid gap-6 lg:grid-cols-3">
        <SignupsCard signups={overview.signups} className="lg:col-span-1" />
        <AttentionCard attention={overview.attention} className="lg:col-span-2" />
      </div>
      <ActivityCard events={overview.recentEvents} />
    </motion.div>
  )
}

function KpiTiles({ overview }: { overview: PlatformOverview }) {
  const { totals } = overview
  const tiles = [
    { label: 'Tenants', value: totals.tenants, status: undefined },
    { label: 'Active', value: totals.byStatus.active ?? 0, status: 'active' },
    { label: 'Paused', value: totals.byStatus.paused ?? 0, status: 'paused' },
    {
      label: 'Pending deletion',
      value: totals.byStatus.pending_deletion ?? 0,
      status: 'pending_deletion',
    },
  ]
  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
      {tiles.map((tile) => (
        <Link
          key={tile.label}
          to="/platform/tenants"
          search={tile.status ? { status: tile.status } : {}}
          className="rounded-2xl border border-border bg-card p-4 shadow-sm transition-colors hover:bg-accent/40"
        >
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {tile.label}
          </div>
          <div className="mt-2 text-3xl font-bold tabular-nums">{tile.value}</div>
        </Link>
      ))}
      <div className="col-span-2 rounded-2xl border border-border bg-card p-4 shadow-sm lg:col-span-1">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          By tier
        </div>
        <dl className="mt-2 space-y-1 text-sm">
          {(['personal', 'business', 'enterprise'] as const).map((tier) => (
            <div key={tier} className="flex justify-between">
              <dt className="capitalize text-muted-foreground">{tier}</dt>
              <dd className="font-semibold tabular-nums">{totals.byTier[tier] ?? 0}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  )
}

function SignupsCard({
  signups,
  className,
}: {
  signups: PlatformOverview['signups']
  className?: string
}) {
  const max = Math.max(1, ...signups.map((w) => w.count))
  const total = signups.reduce((sum, w) => sum + w.count, 0)
  const barWidth = 100 / signups.length
  return (
    <section className={cn('rounded-2xl border border-border bg-card p-5 shadow-sm', className)}>
      <h3 className="text-sm font-semibold">New tenants</h3>
      <p className="text-xs text-muted-foreground">
        {total} in the last {signups.length} weeks
      </p>
      <svg
        viewBox="0 0 100 40"
        preserveAspectRatio="none"
        role="img"
        aria-label="New tenants per week"
        className="mt-4 h-32 w-full text-primary"
      >
        {signups.map((week, i) => {
          const height = (week.count / max) * 36
          return (
            <rect
              key={week.weekStart}
              x={i * barWidth + barWidth * 0.15}
              y={40 - Math.max(height, 0.8)}
              width={barWidth * 0.7}
              height={Math.max(height, 0.8)}
              rx={1}
              fill="currentColor"
              opacity={week.count === 0 ? 0.2 : 1}
            >
              <title>{`Week of ${week.weekStart.slice(0, 10)}: ${week.count}`}</title>
            </rect>
          )
        })}
      </svg>
      <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
        <span>{shortDate(signups[0]?.weekStart)}</span>
        <span>{shortDate(signups.at(-1)?.weekStart)}</span>
      </div>
    </section>
  )
}

function AttentionCard({
  attention,
  className,
}: {
  attention: PlatformOverview['attention']
  className?: string
}) {
  const empty =
    attention.pendingDeletion.length +
      attention.paused.length +
      attention.expiringLicenses.length +
      attention.unverifiedDomains.length ===
    0
  return (
    <section className={cn('rounded-2xl border border-border bg-card p-5 shadow-sm', className)}>
      <h3 className="flex items-center gap-2 text-sm font-semibold">
        <AlertTriangle className="h-4 w-4 text-warning" />
        Needs attention
      </h3>
      {empty ? (
        <p className="mt-4 text-sm text-muted-foreground">Nothing needs attention right now.</p>
      ) : (
        <div className="mt-4 grid gap-5 sm:grid-cols-2">
          <AttentionGroup
            title="Pending deletion"
            Icon={Trash2}
            items={attention.pendingDeletion.map((t) => ({
              key: t.id,
              tenantId: t.id,
              label: t.name,
              note: t.scheduledDeleteAt ? `deletes ${relativeDays(t.scheduledDeleteAt)}` : '',
            }))}
          />
          <AttentionGroup
            title="Paused"
            Icon={PauseCircle}
            items={attention.paused.map((t) => ({
              key: t.id,
              tenantId: t.id,
              label: t.name,
              note: t.updatedAt ? `since ${shortDate(t.updatedAt)}` : '',
            }))}
          />
          <AttentionGroup
            title="Licenses expiring within 30 days"
            Icon={CalendarClock}
            items={attention.expiringLicenses.map((l) => ({
              key: `${l.tenantId}-${l.moduleKey}`,
              tenantId: l.tenantId,
              label: `${l.tenantName} · ${l.moduleKey}`,
              note: relativeDays(l.expiresAt),
            }))}
          />
          <AttentionGroup
            title="Email domain not verified"
            Icon={ShieldQuestion}
            items={attention.unverifiedDomains.map((t) => ({
              key: t.id,
              tenantId: t.id,
              label: t.name,
              note: t.emailDomain ?? '',
            }))}
          />
        </div>
      )}
    </section>
  )
}

function AttentionGroup({
  title,
  Icon,
  items,
}: {
  title: string
  Icon: typeof Trash2
  items: Array<{ key: string; tenantId: string; label: string; note: string }>
}) {
  if (items.length === 0) return null
  return (
    <div>
      <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {title}
      </h4>
      <ul className="mt-2 space-y-1">
        {items.map((item) => (
          <li key={item.key}>
            <Link
              to="/platform/tenants/$tenantId"
              params={{ tenantId: item.tenantId }}
              className="flex items-baseline justify-between gap-3 rounded-lg px-2 py-1.5 text-sm transition-colors hover:bg-accent/40"
            >
              <span className="truncate font-medium">{item.label}</span>
              <span className="shrink-0 text-xs text-muted-foreground">{item.note}</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}

function ActivityCard({ events }: { events: PlatformOverview['recentEvents'] }) {
  return (
    <section className="rounded-2xl border border-border bg-card p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Recent activity</h3>
        <Link to="/platform/audit" className="text-xs font-medium text-primary hover:underline">
          View audit log
        </Link>
      </div>
      {events.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">No activity recorded yet.</p>
      ) : (
        <ul className="mt-3 divide-y divide-border">
          {events.map((event) => (
            <li key={event.id} className="flex items-baseline justify-between gap-4 py-2 text-sm">
              <span className="min-w-0 truncate">
                <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{event.action}</code>
                {event.targetLabel ? (
                  <span className="ml-2 font-medium">{event.targetLabel}</span>
                ) : null}
                <span className="ml-2 text-xs text-muted-foreground">
                  {event.actorEmail ?? 'system'}
                </span>
                {event.status === 'failure' ? (
                  <span className="ml-2 text-xs font-semibold text-destructive">failed</span>
                ) : null}
              </span>
              <time dateTime={event.occurredAt} className="shrink-0 text-xs text-muted-foreground">
                {new Date(event.occurredAt).toLocaleString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </time>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function shortDate(iso: string | null | undefined): string {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function relativeDays(iso: string): string {
  const days = Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000)
  if (days <= 0) return 'today'
  return days === 1 ? 'in 1 day' : `in ${days} days`
}
